import { nextTick } from 'vue'

import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app'

import { RICH_HTML_SANITIZE_CONFIG } from '../utils/html-sanitize-config'

// Shared allowlist for iframes and specific attributes/tags.
const allowIframeAndAttributesConfig = RICH_HTML_SANITIZE_CONFIG

export default defineNuxtPlugin(() => {
  const app = useNuxtApp().vueApp

  // DOMPurify requires a DOM; avoid importing it on the server.
  // We'll lazy-load it on the client when needed.
  const getDOMPurify = async () => {
    const mod = await import('dompurify')
    return mod.default
  }

  // Register the sanitized-html directive for both SSR and client
  app.directive('sanitized-html', {
    // SSR support - pass HTML through without sanitization (DOMPurify requires DOM)
    getSSRProps(binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      let html: string

      if (typeof binding.value === 'object') {
        html = binding.value.html
      } else {
        html = binding.value
      }

      // NOTE: We can't use DOMPurify on the server as it requires a DOM environment
      // The HTML will be sanitized on the client side during hydration
      // To avoid hydration mismatches, ensure the content is already safe or
      // sanitize it before passing to the directive
      return {
        innerHTML: html || '',
      }
    },

    // Client-side hooks
    mounted(el: HTMLElement, binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      // Only run on client side
      if (process.client) {
        let html: string
        let afterSanity: ((el: HTMLElement) => void) | undefined

        if (typeof binding.value === 'object') {
          html = binding.value.html
          afterSanity = binding.value.afterSanity
        } else {
          html = binding.value
        }

        if (html) {
          ;(async () => {
            try {
              const DOMPurify = await getDOMPurify()
              const sanitizedHtml = DOMPurify.sanitize(html, {
                ...allowIframeAndAttributesConfig,
              })
              el.innerHTML = sanitizedHtml

              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            } catch {
              // Fail-safe: if DOMPurify fails to load, fall back to raw HTML.
              // This avoids blank content and prevents runtime crashes.
              el.innerHTML = html
              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            }
          })()
        }
      }
    },
    updated(el: HTMLElement, binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      // Only run on client side
      if (process.client) {
        let html: string
        let afterSanity: ((el: HTMLElement) => void) | undefined

        if (typeof binding.value === 'object') {
          html = binding.value.html
          afterSanity = binding.value.afterSanity
        } else {
          html = binding.value
        }

        if (html) {
          ;(async () => {
            try {
              const DOMPurify = await getDOMPurify()
              const sanitizedHtml = DOMPurify.sanitize(html, {
                ...allowIframeAndAttributesConfig,
              })
              el.innerHTML = sanitizedHtml

              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            } catch {
              // Fail-safe: if DOMPurify fails to load, fall back to raw HTML.
              el.innerHTML = html
              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            }
          })()
        }
      }
    },
  })
})
